import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  FileText, Filter, RefreshCw, Lock, Landmark, Search,
  ChevronRight, Layers, History, ArrowUpRight,
} from 'lucide-react';

/**
 * Statement Registry (Wave 2-C-1).
 *
 * The official registry of every imported bank statement. Joins the
 * statement, bank account, line counts, match progress and health
 * into a single management view. Filters: account, health band, year,
 * month. Each row links to the statement detail or its import session.
 */

const BAND_TONE = {
  green:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  amber:  'bg-amber-100   text-amber-800   border-amber-300',
  red:    'bg-red-100     text-red-800     border-red-300',
};


function HealthBadge({ band, score }) {
  const icon = band === 'green' ? '✓' : band === 'amber' ? '⚠' : '✗';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${BAND_TONE[band] || BAND_TONE.red}`}>
      {icon} {Math.round((score || 0) * 100)}%
    </span>
  );
}


function MatchProgress({ pct }) {
  const p = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const cls = p >= 95 ? 'bg-emerald-500' : p >= 80 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full ${cls}`} style={{ width: `${p}%` }} />
      </div>
      <span className="font-mono text-xs">{p}%</span>
    </div>
  );
}


export default function StatementRegistry() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ green: 0, amber: 0, red: 0, locked: 0 });
  const [accounts, setAccounts] = useState([]);
  const [filterAccount, setFilterAccount] = useState('');
  const [filterBand, setFilterBand] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.get('/bank-import/registry', {
        params: {
          bank_account_id: filterAccount || undefined,
          health_band:     filterBand    || undefined,
          period_year:     filterYear    || undefined,
          period_month:    filterMonth   || undefined,
        },
      });
      setItems(data.items || []);
      setCounts(data.counts || { green: 0, amber: 0, red: 0, locked: 0 });
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error('تَعَذَّر تَحميل السِجِل');
    } finally { setBusy(false); }
  }, [filterAccount, filterBand, filterYear, filterMonth]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(s =>
      (s.filename || '').toLowerCase().includes(q) ||
      (s.bank_name || '').toLowerCase().includes(q) ||
      (s.account_number || '').toLowerCase().includes(q) ||
      (s.template_key || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <div className="p-4 max-w-7xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="w-6 h-6 text-indigo-600" />
            سِجِل الكُشوف البَنكية
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            المَرجِع الرَّسمي لِكُل كَشف مُستَورَد — مَع الصِحَّة، المُطابَقَة، وَالحالَة.
          </p>
        </div>
        <button onClick={load} disabled={busy}
                data-testid="registry-refresh"
                className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2">
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> تَحديث
        </button>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="rounded-xl border bg-emerald-50/60 border-emerald-200 p-3" data-testid="count-green">
          <div className="text-[10px] uppercase font-semibold text-emerald-700">صَحي</div>
          <div className="text-2xl font-bold text-emerald-900 font-mono">{counts.green}</div>
        </div>
        <div className="rounded-xl border bg-amber-50/60 border-amber-200 p-3" data-testid="count-amber">
          <div className="text-[10px] uppercase font-semibold text-amber-700">مُتَوَسِّط</div>
          <div className="text-2xl font-bold text-amber-900 font-mono">{counts.amber}</div>
        </div>
        <div className="rounded-xl border bg-red-50/60 border-red-200 p-3" data-testid="count-red">
          <div className="text-[10px] uppercase font-semibold text-red-700">ضَعيف</div>
          <div className="text-2xl font-bold text-red-900 font-mono">{counts.red}</div>
        </div>
        <div className="rounded-xl border bg-zinc-50 border-zinc-200 p-3" data-testid="count-locked">
          <div className="text-[10px] uppercase font-semibold text-zinc-700">مَقفول</div>
          <div className="text-2xl font-bold text-zinc-900 font-mono">{counts.locked}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-white p-3 mb-4 grid md:grid-cols-5 gap-2">
        <select value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)}
                data-testid="filter-account"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white">
          <option value="">كُل الحِسابات</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {a.number}</option>)}
        </select>
        <select value={filterBand} onChange={(e) => setFilterBand(e.target.value)}
                data-testid="filter-band"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white">
          <option value="">كُل المُستويات</option>
          <option value="green">✓ صَحي</option>
          <option value="amber">⚠ مُتَوَسِّط</option>
          <option value="red">✗ ضَعيف</option>
        </select>
        <input type="number" placeholder="السَنَة" value={filterYear}
               onChange={(e) => setFilterYear(e.target.value)}
               data-testid="filter-year"
               className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-mono" />
        <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
                data-testid="filter-month"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white">
          <option value="">كُل الأَشهُر</option>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(m =>
            <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="relative">
          <Search className="absolute start-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 data-testid="registry-search"
                 placeholder="بَحث…" className="ps-7 pe-3 py-1.5 text-sm rounded-lg border border-slate-300 w-full" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-start px-3 py-2">الملَف</th>
                <th className="text-start px-3 py-2">البَنك / الحِساب</th>
                <th className="text-start px-3 py-2">الفَترَة</th>
                <th className="text-end px-3 py-2">العَدَد</th>
                <th className="text-start px-3 py-2">المُطابَقَة</th>
                <th className="text-start px-3 py-2">الصِحَّة</th>
                <th className="text-start px-3 py-2">الحالة</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-slate-400">لا تُوجَد كُشوف مُطابِقَة للتَّصفيَة.</td></tr>
              ) : filtered.map((s) => (
                <tr key={s.id} className="border-t hover:bg-slate-50/60" data-testid={`registry-row-${s.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">{s.filename || '—'}</div>
                    {s.template_key && <div className="text-[10px] text-slate-500 mt-0.5">{s.template_key}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-slate-800">{s.bank_name || '—'}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{s.account_number || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                    {s.period_start || '—'} → {s.period_end || '—'}
                  </td>
                  <td className="px-3 py-2 text-end font-mono">{s.lines_count}</td>
                  <td className="px-3 py-2"><MatchProgress pct={s.match_rate} /></td>
                  <td className="px-3 py-2"><HealthBadge band={s.health_band} score={s.health_score} /></td>
                  <td className="px-3 py-2">
                    {s.is_locked ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 bg-zinc-100 border border-zinc-300 px-2 py-0.5 rounded-full">
                        <Lock className="w-3 h-3" /> مَقفول
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-300 px-2 py-0.5 rounded-full">
                        {s.status === 'matched' ? 'مُطابَق' : 'مُستَورد'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    <a href={`/bank-reconciliation?stmt=${s.id}`}
                       data-testid={`open-stmt-${s.id}`}
                       className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-semibold">
                      فَتح <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
